import { jsx as _jsx } from "react/jsx-runtime";
import { ContentItemInternal } from '#pplx/frontend/aether/components/ContentItem/index.js';
import { useMenuContext } from '#pplx/frontend/aether/components/internal/menu/useMenuContext.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import classNames from 'classnames';
const VALID_MENU_ITEM_ROLES = new Set(['menuitem', 'menuitemcheckbox', 'menuitemradio']);
/**
 * A base component for menu link items. This component renders as an anchor tag
 * and is meant to be extended by other menu link components.
 *
 * Aether-internal: this is meant to be used by the Menu component only.
 */
export function MenuLinkBase({ ref, href, target, rel, children, leadingAccessory, trailingAccessory, subtitle, textOverflow, role = 'menuitem', 
// We intentionally capture the rest of props that could be cloned/passed in by 3rd-party libraries
// such as Radix UI for their own internal usage such as data-state, onFocus, onBlur, etc.
...props }) {
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to the ContentItemInternal.
    const __radixProps = useRadixProps(props);
    // Radix UI uses the presence of the data-disabled attribute (with an empty string or "true" value)
    // to indicate that the item is disabled.
    // In mobile mode (dialog), we use aria-disabled instead.
    const disabled = __radixProps['data-disabled'] === '' ||
        __radixProps['data-disabled'] === 'true' ||
        props['aria-disabled'] === true ||
        props['aria-disabled'] === 'true';
    // Radix UI uses the presence of the data-highlighted attribute (with an empty string value or "true" value)
    // to indicate that the item is highlighted (i.e: selected).
    const highlighted = __radixProps['data-highlighted'] === '' || __radixProps['data-highlighted'] === 'true';
    // We show the focus ring only when the role isn't one of the standard menuitem roles
    const shouldShowFocusRing = !VALID_MENU_ITEM_ROLES.has(role);
    const menuContext = useMenuContext();
    const isMobileBottomSheet = Boolean(menuContext?.isMobileBottomSheet);
    // Renders as a standard menu item ff no href is provided
    if (!href) {
        return (_jsx(ContentItemInternal, { ref: ref, role: "menuitem", disabled: disabled, highlighted: highlighted, shouldShowFocusRing: shouldShowFocusRing, leadingAccessory: leadingAccessory, trailingAccessory: trailingAccessory, subtitle: subtitle, textOverflow: textOverflow, size: isMobileBottomSheet ? 'large' : 'default', rounded: true, className: classNames(props.className, 
            // Ensure min-width is always equal or greater to the trigger (minus side padding)
            'min-w-[calc(var(--radix-dropdown-menu-trigger-width)-theme(spacing.2))]'), ...__radixProps, children: children }));
    }
    return (_jsx(ContentItemInternal, { ref: ref, 
        // Render the link as a menuitem role because this is part of a menu pattern.
        // Ensure we pass anchor props down as well.
        role: "menuitem", href: href, target: target, rel: rel, disabled: disabled, highlighted: highlighted, shouldShowFocusRing: shouldShowFocusRing, leadingAccessory: leadingAccessory, trailingAccessory: trailingAccessory, size: isMobileBottomSheet ? 'large' : 'default', subtitle: subtitle, textOverflow: textOverflow, rounded: true, className: classNames(props.className, 
        // Ensure min-width is always equal or greater to the trigger (minus side padding)
        'min-w-[calc(var(--radix-dropdown-menu-trigger-width)-theme(spacing.2))]'), ...__radixProps, children: children }));
}
//# sourceMappingURL=MenuLinkBase.js.map